<ng-container *ngIf="thread$ | async as vm">
  <header class="thread-header" *ngIf="!embedded">
    <button mat-icon-button class="back" (click)="back.emit()" aria-label="Back to conversations"><mat-icon>arrow_back</mat-icon></button>
    <mat-icon class="kind" *ngIf="conversation.type === 'channel'">{{ conversation.visibility === 'private' ? 'lock' : 'groups' }}</mat-icon>
    <mat-icon class="kind" *ngIf="conversation.type === 'group_dm'">group</mat-icon>
    <div class="title">
      <h3>{{ title() }}</h3>
      <span class="description" *ngIf="conversation.description">{{ conversation.description }}</span>
    </div>
    <button mat-icon-button (click)="openInfo()" matTooltip="Details and members" aria-label="Conversation details"><mat-icon>info</mat-icon></button>
  </header>

  <div class="banner admin" *ngIf="conversation.access === 'admin'" [class.expanded]="bannerExpanded" (click)="bannerExpanded = !bannerExpanded">
    <mat-icon>admin_panel_settings</mat-icon>
    <span class="banner-text">You are viewing this group as a practice admin. You are not a member, and opening it is recorded.</span>
  </div>
  <div class="banner patient" *ngIf="conversation.type === 'patient'" [class.expanded]="bannerExpanded" (click)="bannerExpanded = !bannerExpanded">
    <mat-icon>personal_injury</mat-icon>
    <span class="banner-text">Patient chat. Everyone in the practice can read it, and it shows on the patient's timeline. It stays inside the
    practice — a referral chat about this patient is a separate conversation.</span>
  </div>
  <div class="banner patient" *ngIf="conversation.type === 'referral'" [class.expanded]="bannerExpanded" (click)="bannerExpanded = !bannerExpanded">
    <mat-icon>swap_horiz</mat-icon>
    <span class="banner-text">Referral chat with {{ conversation.referral?.counterpart || 'the other side' }}, about this patient only. It is separate
    from the practice's own patient chat, which they never see. Every message here is on the patient's timeline.<ng-container *ngIf="conversation.referral?.filesByGrant && !conversation.referral?.filesAllowed"> Each message with files uses one of the referral's documents.</ng-container><ng-container *ngIf="!filesAllowed"> Text only: files go only between MedicAI practices.</ng-container></span>
  </div>
  <div class="banner patient" *ngIf="conversation.type === 'network'" [class.expanded]="bannerExpanded" (click)="bannerExpanded = !bannerExpanded">
    <mat-icon>share</mat-icon>
    <span class="banner-text">Direct chat with {{ counterpartLabel() }}, outside your practice. Patients you link here are not shared until you refer
    them.<ng-container *ngIf="!filesAllowed"> Text only: files go only between MedicAI practices.</ng-container></span>
  </div>
  <div class="banner patient" *ngIf="conversation.type === 'support'" [class.expanded]="bannerExpanded" (click)="bannerExpanded = !bannerExpanded">
    <mat-icon>support_agent</mat-icon>
    <span class="banner-text">MedicAI Support. Only you and the support team see this chat; messages stay as sent.</span>
  </div>
  <!-- The patient's own chat from the patient app (tasks/patient-app.md §8): their words, text only, and the patient
       keeps the history if the link ends. -->
  <div class="banner patient" *ngIf="conversation.type === 'patient_direct'" [class.expanded]="bannerExpanded" (click)="bannerExpanded = !bannerExpanded">
    <mat-icon>phone_iphone</mat-icon>
    <span class="banner-text">{{ conversation.patientDirect?.patientName || 'The patient' }} writes here from the patient app. Every message is on
    their record. Text only.</span>
  </div>
  <!-- A billing chat (2026-10-05): who is on the other side, and that nothing else of the practice's chat is theirs. -->
  <div class="banner" *ngIf="conversation.type === 'billing' && !conversation.readOnlyReason">
    <mat-icon>receipt_long</mat-icon>
    <span class="banner-text">{{ conversation.billing?.side === 'practice'
      ? 'With ' + (conversation.billing?.counterpartName || 'your billing team') + ', your billing team on MedicAI Connect. They see only billing chats.'
      : 'With ' + (conversation.billing?.counterpartName || 'the practice') + ' on MedicAI. You see only billing chats with them.' }}</span>
  </div>
  <div class="banner" *ngIf="conversation.readOnlyReason">
    <mat-icon>lock</mat-icon>
    <ng-container *ngIf="conversation.type === 'billing'">
      This billing team no longer bills the practice: the chat is read-only and keeps its history.
    </ng-container>
    <ng-container *ngIf="conversation.type === 'patient_direct'">
      This patient is no longer linked to the patient app: the chat is read-only and keeps its history.
    </ng-container>
    <ng-container *ngIf="conversation.type !== 'patient_direct' && conversation.type !== 'billing'">
      Sharing has stopped for this referral: the chat is read-only and keeps its history.
    </ng-container>
  </div>
  <div class="banner" *ngIf="conversation.archivedAt">
    <mat-icon>inventory_2</mat-icon>
    This group is archived and read-only.
  </div>

  <!-- Pinned messages (at most three): one at a time; a tap goes to it and shows the next. -->
  <button type="button" class="pin-bar" *ngIf="pins[pinIndex] as pin" (click)="goToPin()" [attr.aria-label]="'Go to pinned message: ' + pin.preview">
    <span class="pin-dots" *ngIf="pins.length > 1" aria-hidden="true"><i *ngFor="let p of pins; let i = index" [class.on]="i === pinIndex"></i></span>
    <mat-icon class="pin-icon">push_pin</mat-icon>
    <span class="pin-text">
      <span class="pin-title">Pinned{{ pins.length > 1 ? ' · ' + (pinIndex + 1) + ' of ' + pins.length : '' }}<ng-container *ngIf="pin.senderName"> · {{ pin.senderName }}</ng-container></span>
      <span class="pin-preview">{{ pin.preview }}</span>
    </span>
    <span class="pin-unpin" *ngIf="writable()" role="button" tabindex="0" (click)="unpinShown($event)" (keydown.enter)="unpinShown($event)"
      matTooltip="Unpin" aria-label="Unpin"><mat-icon>close</mat-icon></span>
  </button>

  <!-- Files dragged over any part of the conversation: one clear target, the whole pane. -->
  <div class="drop-overlay" *ngIf="dragging" aria-hidden="true">
    <div class="drop-card"><mat-icon>upload_file</mat-icon><span>Drop to send to {{ title() }}</span></div>
  </div>

  <!-- Focusable (not in the tab order) so a click in the messages then a paste lands in this conversation. -->
  <div class="messages" #scroller tabindex="-1" (scroll)="onScroll(vm.thread)" [class.has-filing]="hasFilingLine(vm.rows)">
    <!-- Opened from a search hit: this is an older part of the conversation, not the latest. -->
    <div class="banner historic" *ngIf="isHistoric()">
      <mat-icon>history</mat-icon>
      Showing an older part of this conversation.
      <button mat-button color="primary" (click)="backToLatest()">Jump to latest</button>
    </div>

    <ng-container [ngSwitch]="loader(vm.thread)">
      <div class="loading-first" *ngSwitchCase="'first'" role="status"><mat-spinner diameter="32"></mat-spinner><span>Loading messages…</span></div>
      <div class="loading-older" *ngSwitchCase="'older'" role="status"><mat-spinner diameter="18"></mat-spinner><span>Loading messages…</span></div>
    </ng-container>

    <!-- Nothing said yet (2026-09-22): say so, rather than an empty pane that looks like messages failed to load. -->
    <div class="empty-thread" *ngIf="vm.thread?.loaded && !vm.thread?.loading && !vm.rows.length">
      <mat-icon>forum</mat-icon>
      <strong>No messages yet</strong>
      <span>{{ emptyHint() }}</span>
    </div>

    <ng-container *ngFor="let row of vm.rows; trackBy: trackRow">
      <div class="day-divider" *ngIf="row.kind === 'day'"><span>{{ row.label }}</span></div>

      <!--
        A referral chat's cards (phase 5, owner 2026-09-22), as the referrals dashboard's chat shows them: the summary
        this practice sent (right), what the other side sent or answered (left), and what else they shared (centre).
        From this practice's own records; the other side never sees these through the chat.
      -->
      <div class="rec-line" *ngIf="row.kind === 'record'" [class.out]="row.item.kind === 'referral_sent'" [class.center]="row.item.kind === 'entries'">
        <div class="rec-card">
          <ng-container *ngIf="asSent(row.item) as s">
            <div class="rec-head">
              <mat-icon>send</mat-icon><span class="rec-kind">{{ sentLabel(s) }}</span>
              <span class="rec-urgency" [ngClass]="s.urgency">{{ urgencyLabels[s.urgency] }}</span>
            </div>
            <div class="rec-field"><span class="rec-label">Reason</span>{{ s.reason }}</div>
            <div class="rec-summary" *ngIf="s.summary?.text">{{ s.summary?.text }}</div>
            <div class="rec-meta">
              {{ shareModeLabels[s.shareMode] }}<ng-container *ngIf="s.summary?.fileCount"> · {{ s.summary?.fileCount }} {{ s.summary?.fileCount === 1 ? 'file' : 'files' }}</ng-container>
              <ng-container *ngIf="s.practiceDecision"> · {{ s.practiceDecision.status === 'accepted' ? 'Accepted' : s.practiceDecision.status === 'declined' ? 'Declined' : 'Awaiting their decision' }}</ng-container>
              · {{ s.at | date: 'd MMM y, HH:mm' }}
            </div>
            <div class="rec-actions" *ngIf="s.summary?.text"><button type="button" class="rec-btn" (click)="readLetter(s)">Read the letter</button></div>
          </ng-container>

          <ng-container *ngIf="asReceived(row.item) as rv">
            <div class="rec-head">
              <mat-icon>{{ rv.isReply ? 'reply' : 'call_received' }}</mat-icon>
              <span class="rec-kind">{{ rv.isReply ? 'Their reply' : 'Referral received' }}</span>
              <span class="rec-urgency" [ngClass]="rv.urgency" *ngIf="!rv.isReply">{{ urgencyLabels[rv.urgency] }}</span>
            </div>
            <div class="rec-from">{{ rv.referredByName }}<ng-container *ngIf="rv.practiceName"> · {{ rv.practiceName }}</ng-container></div>
            <div class="rec-field" *ngIf="rv.reason"><span class="rec-label">Reason</span>{{ rv.reason }}</div>
            <div class="rec-summary" *ngIf="rv.summaryText">{{ rv.summaryText }}</div>
            <div class="rec-meta">
              <ng-container *ngIf="!rv.isReply">{{ rv.status === 'accepted' ? 'Accepted' : rv.status === 'declined' ? 'Declined' : 'Awaiting your decision' }} · </ng-container>{{ rv.at | date: 'd MMM y, HH:mm' }}
            </div>
            <div class="rec-actions">
              <button type="button" class="rec-btn" *ngIf="rv.summaryText" (click)="readLetter(rv)">Read the letter</button>
              <button type="button" class="rec-btn primary" *ngIf="rv.canDecide" (click)="decideReceived(rv)">Accept or decline</button>
            </div>
          </ng-container>

          <ng-container *ngIf="asResponse(row.item) as rs">
            <div class="rec-head">
              <mat-icon>assignment_return</mat-icon><span class="rec-kind">Official response</span>
              <span class="rec-status" [ngClass]="rs.reviewStatus">{{ reviewLabel(rs.reviewStatus) }}</span>
            </div>
            <div class="rec-from">{{ rs.authorName || rs.practiceName }}<ng-container *ngIf="rs.title"> · {{ rs.title }}</ng-container></div>
            <div class="rec-summary" *ngIf="rs.preview">{{ rs.preview }}</div>
            <div class="rec-meta"><ng-container *ngIf="rs.noteDate">Dated {{ rs.noteDate | date: 'd MMM y' }} · </ng-container>Received {{ rs.at | date: 'd MMM y, HH:mm' }}</div>
            <div class="rec-actions">
              <!-- The Inbox's own review dialog: validating there is "accept to timeline", with the same permissions. -->
              <button type="button" class="rec-btn primary" *ngIf="rs.canReview" (click)="reviewResponse(rs)">Review and accept to timeline</button>
              <button type="button" class="rec-btn" *ngIf="!rs.canReview" (click)="reviewResponse(rs)">Open</button>
            </div>
          </ng-container>

          <ng-container *ngIf="asEntries(row.item) as en">
            <div class="rec-head"><mat-icon>folder_shared</mat-icon>
              <span class="rec-kind">{{ en.total }} {{ en.total === 1 ? 'entry' : 'entries' }} shared by {{ en.practiceName || 'the practice' }}</span></div>
            <div class="rec-meta"><ng-container *ngIf="en.pending">{{ en.pending }} awaiting review · </ng-container>latest {{ en.at | date: 'd MMM y, HH:mm' }}</div>
            <div class="rec-actions" *ngIf="en.pending"><button type="button" class="rec-btn" (click)="openInbox()">Review in the Inbox</button></div>
          </ng-container>
        </div>
      </div>

      <ng-container *ngIf="row.kind === 'message'">
        <div class="msg system" *ngIf="row.message.kind === 'system'">
          {{ row.message.body }} · {{ row.message.createdAt | date: 'shortTime' }}
        </div>

        <!--
          The patient banner (owner, 2026-10-02): a message that links patients gets a full-width bar ABOVE it, naming them
          with their chat and file, as a divider where the patient being talked about changes. A run of messages about the
          same patient has one bar (patient-context.ts); in a network chat every linking message keeps its own, for Share.
        -->
        <div class="patient-context" *ngIf="row.message.kind !== 'system' && (row.context || referredFor(row.message.id).length)"
          [class.filing-from]="row.filing?.above">
        <!-- The chip opens the patient's chat, the button beside it their file. In a network chat a patient shared with
             the other side (its referral chat) shows once, as shared; one only in this practice says so, with Share
             (owner, 2026-09-22). -->
        <div class="linked-to" *ngIf="visibleLinks(row.message).length">
          <span class="linked-to-item" *ngFor="let l of visibleLinks(row.message)">
            <ng-container *ngIf="l.kind === 'referral'; else ownLink">
              <button type="button" class="chip shared" [class.off]="l.unavailable" (click)="openLink(l)"
                [matTooltip]="l.unavailable ? 'No longer shared with ' + (conversation.network?.counterpart?.name || 'them') : 'Open ' + (l.name || 'the patient') + '’s file'">
                <mat-icon>share</mat-icon>{{ l.name || 'Patient' }} · {{ l.unavailable ? 'no longer shared' : 'shared with ' + (conversation.network?.counterpart?.name || 'them') }}
              </button>
              <button type="button" class="file" (click)="openLinked(l.conversationId)"
                [matTooltip]="'Open the referral chat about ' + (l.name || 'the patient')" [attr.aria-label]="'Open the referral chat about ' + (l.name || 'the patient')">
                <mat-icon>forum</mat-icon>
              </button>
            </ng-container>
            <ng-template #ownLink>
              <button type="button" class="chip" (click)="openLinked(l.conversationId)" [matTooltip]="'Open ' + (l.name || 'the patient') + '’s chat'">
                <mat-icon>personal_injury</mat-icon>{{ l.name || 'Patient' }}
              </button>
              <button type="button" class="file" *ngIf="l.patientRef" (click)="openPatientFile(l.patientRef)"
                [matTooltip]="'Open ' + (l.name || 'the patient') + '’s file'" [attr.aria-label]="'Open ' + (l.name || 'the patient') + '’s file'">
                <mat-icon>visibility</mat-icon>
              </button>
              <!-- What the other side can have of it (2026-09-22): referred and sharing, so one tap links it; referred to
                   someone who has not joined yet; or not referred, with Refer. -->
              <ng-container *ngIf="notShared(row.message, l)">
                <ng-container [ngSwitch]="shareStateOf(l)">
                  <span class="not-shared" *ngSwitchCase="'share'">
                    Referred to {{ conversation.network?.counterpart?.name || 'them' }}
                    <button type="button" class="share-now" (click)="shareLink(row.message, l)"
                      [attr.aria-label]="'Share the link to ' + (l.name || 'the patient') + ' with ' + (conversation.network?.counterpart?.name || 'them')">Share with {{ conversation.network?.counterpart?.name || 'them' }}</button>
                  </span>
                  <span class="not-shared" *ngSwitchCase="'awaiting'">
                    Referred to {{ conversation.network?.counterpart?.name || 'them' }} · not joined yet
                  </span>
                  <span class="not-shared" *ngSwitchCase="'checking'">Checking…</span>
                  <span class="not-shared" *ngSwitchDefault>
                    Not shared with {{ conversation.network?.counterpart?.name || 'them' }}
                    <button type="button" class="share-now" (click)="shareLink(row.message, l)"
                      [attr.aria-label]="'Share ' + (l.name || 'the patient') + ' with ' + (conversation.network?.counterpart?.name || 'them')">{{ canRefer ? 'Refer' : 'Share' }}</button>
                  </span>
                </ng-container>
              </ng-container>
            </ng-template>
          </span>
          <button type="button" class="unlink-run" *ngIf="actionsOffered && !row.message.pending && !row.message.deletedAt" (click)="addRunToNotes(row.message)"
            matTooltip="Add these messages to the patient's notes" aria-label="Add these messages to the patient's notes"><mat-icon>note_add</mat-icon></button>
          <button type="button" class="unlink-run" *ngIf="!row.message.pending && !row.message.deletedAt" (click)="unlinkRun(row.message)"
            matTooltip="Unlink these messages" aria-label="Unlink these messages"><mat-icon>link_off</mat-icon></button>
        </div>

        <!-- Network chat (2026-09-22): patients from this message referred to someone who has not joined yet, so there is no
             referral chat to link into; it links itself once they have (the device remembers). Sender only. -->
        <div class="linked-to referred-to" *ngIf="referredFor(row.message.id).length">
          <span class="linked-to-item" *ngFor="let p of referredFor(row.message.id); trackBy: trackPrompt">
            <button type="button" class="chip" (click)="openPatientFile(p.patientRef)" [matTooltip]="'Open ' + p.title + '’s file'">
              <mat-icon>share</mat-icon>{{ p.title }} · referred to {{ conversation.network?.counterpart?.name || 'them' }}<ng-container *ngIf="p.since"> · {{ p.since | date: 'd MMM' }}</ng-container> · shared once they join
            </button>
          </span>
        </div>
        </div>

        <!--
          A line, as the referrals dashboard draws one (2026-09-22): the other side's avatar and bubble on the left, yours
          on the right with no avatar. A run from one sender sits close; only its first bubble has the tail and the name.
        -->
        <!--
          A task, booking or note made from the chat: a card across the thread, not a bubble, outlined in the colour of
          where it stands (as RelayMD draws it). Right click or a long press opens the card's own menu.
        -->
        <div class="line card-line" *ngIf="row.message.kind !== 'system' && isCard(row.message) && row.message.card as card"
          [class.filed]="row.filing" [class.filed-above]="row.filing?.above" [class.filed-below]="row.filing?.below">
          <div class="msg card-msg" [attr.data-seq]="row.message.seq" [class.found]="row.message.seq === pinnedSeq()" [class.pending]="row.message.pending">
            <div class="action-card" [style.--card]="actionCardOutline(card)" [class.closed]="actionCardClosed(card)"
              (contextmenu)="openActions(row.message, $event)" (appLongPress)="openActions(row.message)">
              <div class="ac-head">
                <span class="ac-kind">{{ actionCardKind(card) }}</span>
                <span class="ac-patient">&nbsp;· {{ card.patientName }}</span>
                <span class="ac-meta">{{ row.message.sender?.principalId === vm.me ? 'You' : row.message.sender?.name }} · {{ row.message.createdAt | date: 'shortTime' }}<span class="stamp-tick" *ngIf="tick(row.message) as t" [class.read]="t === 'read'" [class.clickable]="canListReceipts()" [matTooltip]="tickLabel(t)" (click)="showReceipts(row.message)" [attr.aria-label]="tickLabel(t)"><mat-icon>{{ tickIcon(t) }}</mat-icon></span></span>
              </div>
              <div class="ac-body">
                <span class="ac-icon"><mat-icon>{{ actionCardIcon(card) }}</mat-icon></span>
                <span class="ac-text">
                  <span class="ac-title">{{ card.title }}</span>
                  <span class="ac-status" *ngIf="actionCardStatus(card) as status" [class.tinted]="actionCardStatusTinted(card)">{{ status }}</span>
                </span>
              </div>
              <div class="ac-buttons">
                <button type="button" class="ac-pill" (click)="openActionCard(card)">Open</button>
                <button type="button" class="ac-pill filled" *ngIf="card.type === 'task' && card.status !== 'done'" [disabled]="completing.has(card.recordId)"
                  (click)="completeActionCard(card)" aria-label="Mark the task done">Mark done</button>
                <ng-container *ngIf="bookingButtons(card) as b">
                  <button type="button" class="ac-pill filled confirm" *ngIf="b.confirm" [disabled]="completing.has(card.recordId)"
                    (click)="setBookingStatus(card, 'confirmed')" aria-label="Confirm the appointment">Confirm</button>
                  <button type="button" class="ac-pill cancel" *ngIf="b.cancel" [disabled]="completing.has(card.recordId)"
                    (click)="setBookingStatus(card, 'cancelled')" aria-label="Cancel the appointment">Cancel</button>
                </ng-container>
              </div>
            </div>

            <!-- The card's own menu: the record first, then what a card can do as a message. -->
            <div class="card-menu" [class.up]="cardMenuUp" *ngIf="actionsFor === row.message.id && cardMenu(row.message) as a" role="menu">
              <button type="button" role="menuitem" *ngIf="a.open" (click)="openActionCard(card)"><mat-icon>{{ actionCardIcon(card) }}</mat-icon>{{ card.type === 'appointment' ? 'Open booking' : card.type === 'note' ? 'Open note' : 'Open task' }}</button>
              <button type="button" role="menuitem" *ngIf="a.markDone" (click)="completeActionCard(card)"><mat-icon>done_all</mat-icon>Mark done</button>
              <button type="button" role="menuitem" *ngIf="a.confirm" (click)="setBookingStatus(card, 'confirmed')"><mat-icon>done_all</mat-icon>Confirm</button>
              <button type="button" role="menuitem" class="danger" *ngIf="a.cancel" (click)="setBookingStatus(card, 'cancelled')"><mat-icon>close</mat-icon>Cancel appointment</button>
              <button type="button" role="menuitem" *ngIf="a.reply" (click)="startReply(row.message)"><mat-icon>reply</mat-icon>Reply</button>
              <button type="button" role="menuitem" *ngIf="a.pin" (click)="togglePin(row.message)"><mat-icon>push_pin</mat-icon>{{ isPinned(row.message) ? 'Unpin' : 'Pin' }}</button>
              <button type="button" role="menuitem" *ngIf="a.info" (click)="showReceipts(row.message)"><mat-icon>done_all</mat-icon>Read by</button>
              <button type="button" role="menuitem" class="danger" *ngIf="a.delete" (click)="remove(row.message)"><mat-icon>delete</mat-icon>Delete</button>
            </div>
          </div>
        </div>

        <div class="line" *ngIf="row.message.kind !== 'system' && !isCard(row.message)" [class.grouped]="row.grouped" [class.out]="row.message.sender?.principalId === vm.me"
          [class.filed]="row.filing" [class.filed-above]="row.filing?.above" [class.filed-below]="row.filing?.below">
          <ng-container *ngIf="row.message.sender?.principalId !== vm.me">
            <span class="line-avatar" *ngIf="!row.grouped" [style.--hue]="senderHue(row.message)">
              <img *ngIf="lineAvatar(row.message) as src" [src]="src" alt="" loading="lazy" />
              <span class="initials" *ngIf="!lineAvatar(row.message)">{{ senderInitials(row.message) }}</span>
            </span>
            <span class="line-avatar gap" *ngIf="row.grouped"></span>
          </ng-container>

        <div class="msg" [attr.data-seq]="row.message.seq" [class.found]="row.message.seq === pinnedSeq()" [class.grouped]="row.grouped" [class.mine]="row.message.sender?.principalId === vm.me"
          [class.pending]="row.message.pending && !row.message.failed" [class.failed]="row.message.failed" [class.actions-open]="actionsFor === row.message.id"
          (appSwipeRight)="startReply(row.message)" [swipeRightEnabled]="writable() && !row.message.deletedAt && !row.message.pending"
          (appSwipeLeft)="startLink(row.message)" [swipeLeftEnabled]="canLink(row.message)">
          <div class="msg-who" *ngIf="!row.grouped && conversation.type !== 'direct' && row.message.sender?.principalId !== vm.me" [style.--hue]="senderHue(row.message)">{{ row.message.sender?.name }}</div>

          <!-- Right click or a long press opens the message's actions. -->
          <div class="bubble" [class.tail]="!row.grouped" [class.mine]="row.message.sender?.principalId === vm.me"
            (contextmenu)="openActions(row.message, $event)" (appLongPress)="openActions(row.message)">
            <!--
              The way back to where this was said. Not a button when the original is gone: there would be nothing at
              the other end of it. Nor when it was said in someone else's DM: the hub leaves out where (no conversationId).
            -->
            <div class="linked-from" *ngIf="row.message.kind === 'linked_copy' && row.message.linkedFrom as from">
              <mat-icon>link</mat-icon>
              <span>Linked from {{ linkedFromLabel(from) }} by {{ from.linkedBy.name }}</span>
              <span class="flag" *ngIf="from.editedSinceLink && !from.originalDeleted">original edited since</span>
              <span class="flag" *ngIf="from.originalDeleted">original deleted</span>
              <button type="button" class="go" *ngIf="!from.originalDeleted && from.conversationId" (click)="openOriginal(from)"
                [matTooltip]="'Open ' + from.label + ' at this message'">
                <mat-icon>open_in_new</mat-icon>Go to it
              </button>
            </div>

            <!-- Forwarded: where it came from and who wrote it, and the way back when this reader may open the original. -->
            <div class="linked-from forwarded" *ngIf="row.message.kind === 'forwarded' && row.message.forwardedFrom as fwd">
              <mat-icon>forward</mat-icon>
              <span>Forwarded from {{ fwd.label }}<ng-container *ngIf="fwd.originalSender?.name"> · {{ fwd.originalSender?.name }}</ng-container></span>
              <span class="flag" *ngIf="fwd.originalDeleted">original deleted</span>
              <button type="button" class="go" *ngIf="!fwd.originalDeleted && fwd.conversationId" (click)="openForwardedOriginal(fwd)"
                [matTooltip]="'Open ' + fwd.label + ' at this message'">
                <mat-icon>open_in_new</mat-icon>Go to it
              </button>
            </div>

            <div class="reply-ref" *ngIf="row.message.replyToMessageId" [class.clickable]="replySeq(vm.thread, row.message) !== null"
              (click)="goToReply(vm.thread, row.message)" [attr.role]="replySeq(vm.thread, row.message) !== null ? 'button' : null"
              [matTooltip]="replySeq(vm.thread, row.message) !== null ? 'Go to the message this answers' : ''">
              <mat-icon>reply</mat-icon><span class="reply-text">{{ replySnippet(vm.thread, row.message.replyToMessageId) }}</span>
            </div>


            <!-- The message's photos as one grid (one alone, two side by side, one over two, or four with "+N" on the last),
                 its other files under it, and the text below both. -->
            <ng-container *ngIf="!row.message.deletedAt && photos(row.message) as pg">
              <div class="photos" *ngIf="pg.layout !== 'none'" [ngClass]="pg.layout">
                <button type="button" class="tile" *ngFor="let t of pg.tiles" (click)="openPhotos(pg.all, t.index)" [attr.aria-label]="t.label">
                  <img *ngIf="imageUrl(t.attachment) as url" [src]="url" [alt]="t.attachment.fileName" loading="lazy" />
                  <span class="more" *ngIf="t.more" aria-hidden="true">+{{ t.more }}</span>
                </button>
              </div>
            </ng-container>

            <div class="files" *ngIf="!row.message.deletedAt && otherFiles(row.message) as others">
              <ng-container *ngFor="let a of others">
                <ng-container *ngIf="isMedia(a) && mediaUrl(a) as src; else chip">
                  <video *ngIf="isMedia(a) === 'video'" class="player" [src]="src" controls autoplay preload="metadata" [attr.aria-label]="a.fileName"></video>
                  <audio *ngIf="isMedia(a) === 'audio'" class="player" [src]="src" controls autoplay preload="metadata" [attr.aria-label]="a.fileName"></audio>
                </ng-container>
                <ng-template #chip>
                  <button type="button" class="file" *ngIf="isMedia(a)" (click)="play(a)" [matTooltip]="'Play ' + a.fileName">
                    <mat-icon>play_circle</mat-icon><span class="name">{{ a.fileName }}</span><small>{{ fileSize(a.sizeBytes) }}</small>
                  </button>
                  <button type="button" class="file" *ngIf="!isMedia(a)" (click)="openFile(a)" [matTooltip]="'Open ' + a.fileName">
                    <mat-icon>{{ fileIcon(a) }}</mat-icon><span class="name">{{ a.fileName }}</span><small>{{ fileSize(a.sizeBytes) }}</small>
                  </button>
                </ng-template>
              </ng-container>
            </div>

            <!-- One line on purpose: the body keeps whitespace (pre-wrap). Time and ticks float in its last line. -->
            <div class="body" *ngIf="!row.message.deletedAt && row.message.body && !row.message.card"><ng-container *ngFor="let seg of segments(row.message); trackBy: trackSegment"><span class="mention" *ngIf="seg.principalId; else plainText" [class.me]="seg.principalId === vm.me">{{ seg.text }}</span><ng-template #plainText>{{ seg.text }}</ng-template></ng-container><span class="stamp"><span class="stamp-edited" *ngIf="row.message.editedAt">edited</span>{{ row.message.createdAt | date: 'shortTime' }}<span class="stamp-tick" *ngIf="tick(row.message) as t" [class.read]="t === 'read'" [class.clickable]="canListReceipts()" [matTooltip]="tickLabel(t)" (click)="showReceipts(row.message)" [attr.aria-label]="tickLabel(t)"><mat-icon>{{ tickIcon(t) }}</mat-icon></span></span></div>
            <div class="body deleted" *ngIf="row.message.deletedAt"><ng-container *ngIf="unlinkedLabel(row.message.linkedFrom) as unlinked; else wasDeleted"><mat-icon>link_off</mat-icon> {{ unlinked }}</ng-container><ng-template #wasDeleted><mat-icon>block</mat-icon> This message was deleted</ng-template><span class="stamp"><span class="stamp-edited" *ngIf="row.message.editedAt">edited</span>{{ row.message.createdAt | date: 'shortTime' }}<span class="stamp-tick" *ngIf="tick(row.message) as t" [class.read]="t === 'read'" [class.clickable]="canListReceipts()" [matTooltip]="tickLabel(t)" (click)="showReceipts(row.message)" [attr.aria-label]="tickLabel(t)"><mat-icon>{{ tickIcon(t) }}</mat-icon></span></span></div>

            <!-- Support asked for this app's logs: the user answers here, once. -->
            <div class="log-request" *ngIf="canAnswerLogs(row.message) && row.message.logRequest as lr">
              <ng-container *ngIf="lr.status === 'pending'">
                <p>
                  <mat-icon>bug_report</mat-icon>
                  <span>Sharing your app logs helps Support see what went wrong. They contain recent activity and errors from
                    this app on this device, and may include details of patients you had open.</span>
                </p>
                <div class="log-actions">
                  <button mat-stroked-button type="button" (click)="answerLogs(row.message, false)" [disabled]="answeringLogs.has(row.message.id)">Decline</button>
                  <button mat-flat-button color="primary" type="button" (click)="answerLogs(row.message, true)" [disabled]="answeringLogs.has(row.message.id)">
                    <mat-spinner *ngIf="answeringLogs.has(row.message.id)" diameter="16"></mat-spinner>
                    Share logs
                  </button>
                </div>
              </ng-container>
              <p class="answered" *ngIf="lr.status === 'accepted'"><mat-icon>check_circle</mat-icon><span>You shared your app logs.</span></p>
              <p class="answered" *ngIf="lr.status === 'declined'"><mat-icon>block</mat-icon><span>You declined to share your app logs.</span></p>
            </div>

            <!-- No text to float them in (files only): time and ticks on a line of their own. -->
            <div class="stamp-row" *ngIf="!row.message.deletedAt && !row.message.body"><span class="stamp"><span class="stamp-edited" *ngIf="row.message.editedAt">edited</span>{{ row.message.createdAt | date: 'shortTime' }}<span class="stamp-tick" *ngIf="tick(row.message) as t" [class.read]="t === 'read'" [class.clickable]="canListReceipts()" [matTooltip]="tickLabel(t)" (click)="showReceipts(row.message)" [attr.aria-label]="tickLabel(t)"><mat-icon>{{ tickIcon(t) }}</mat-icon></span></span></div>
          </div>

          <div class="read-count" *ngIf="readCount(row.message) as r">Read by {{ r.read }} of {{ r.of }}</div>

          <div class="failed-actions" *ngIf="row.message.failed">
            Not sent.
            <button mat-button color="primary" (click)="retry(row.message)">Retry</button>
            <button mat-button (click)="discard(row.message)">Discard</button>
          </div>

          <div class="reactions" *ngIf="row.message.reactions.length">
            <button class="reaction" *ngFor="let r of row.message.reactions" [class.mine]="reacted(row.message, r.emoji, vm.me)"
              (click)="toggleReaction(row.message, r.emoji, vm.me)" [attr.aria-label]="r.emoji + ' ' + r.principalIds.length">
              {{ r.emoji }} <span>{{ r.principalIds.length }}</span>
            </button>
          </div>

          <div class="actions" *ngIf="!row.message.pending && !row.message.deletedAt">
            <ng-container *ngIf="conversation.access === 'participant'">
              <button *ngFor="let e of quickReactions" class="quick" (click)="toggleReaction(row.message, e, vm.me)" [attr.aria-label]="'React ' + e">{{ e }}</button>
              <button mat-icon-button *ngIf="writable()" (click)="startReply(row.message)" matTooltip="Reply" aria-label="Reply"><mat-icon>reply</mat-icon></button>
            </ng-container>
            <button mat-icon-button *ngIf="canCopy(row.message)" (click)="copy(row.message)" matTooltip="Copy" aria-label="Copy"><mat-icon>content_copy</mat-icon></button>
            <button mat-icon-button *ngIf="canForward(row.message)" (click)="forward(row.message)" matTooltip="Forward" aria-label="Forward"><mat-icon>forward</mat-icon></button>
            <button mat-icon-button *ngIf="canPin(row.message)" (click)="togglePin(row.message)" [class.pin-on]="isPinned(row.message)"
              [matTooltip]="isPinned(row.message) ? 'Unpin' : 'Pin'" [attr.aria-label]="isPinned(row.message) ? 'Unpin' : 'Pin'"><mat-icon>push_pin</mat-icon></button>
            <button mat-icon-button *ngIf="canLink(row.message)" (click)="startLink(row.message)" matTooltip="Link to a patient's chat" aria-label="Link to a patient's chat"><mat-icon>person_add_alt</mat-icon></button>
            <button mat-icon-button *ngIf="canMakeTask(row.message)" (click)="makeTask(row.message)" matTooltip="Make a task" aria-label="Make a task"><mat-icon>add_task</mat-icon></button>
            <button mat-icon-button *ngIf="canMakeTask(row.message)" (click)="makeTask(row.message, 'appointment')" matTooltip="Book for this patient" aria-label="Book for this patient"><mat-icon>event</mat-icon></button>
            <button mat-icon-button *ngIf="canMakeTask(row.message)" (click)="makeTask(row.message, 'note')" matTooltip="Add to notes" aria-label="Add to notes"><mat-icon>note_add</mat-icon></button>
            <button mat-icon-button *ngFor="let t of unlinkTargets(row.message); trackBy: trackUnlink" (click)="unlink(t)" [matTooltip]="t.label" [attr.aria-label]="t.label"><mat-icon>link_off</mat-icon></button>
            <button mat-icon-button *ngIf="canEdit(row.message, vm.me)" (click)="startEdit(row.message)" matTooltip="Edit" aria-label="Edit"><mat-icon>edit</mat-icon></button>
            <button mat-icon-button *ngIf="canDelete(row.message, vm.me)" (click)="remove(row.message)" matTooltip="Delete" aria-label="Delete"><mat-icon>delete</mat-icon></button>
          </div>
        </div>
        </div>

        <!-- Network chat (2026-09-22): a patient linked here that the other side has not been referred. Sender only. -->
        <div class="ask network-ask" *ngIf="promptsFor(row.message.id).length" role="status">
          <div class="ask-title"><mat-icon>help_outline</mat-icon> Only you can see this</div>
          <div class="ask-row" *ngFor="let p of promptsFor(row.message.id); trackBy: trackPrompt">
            <ng-container [ngSwitch]="p.state">
              <span *ngSwitchCase="'checking'" class="network-checking"><mat-spinner diameter="14"></mat-spinner> Checking whether {{ p.title }} is referred to {{ counterpartLabel() }}…</span>
              <span *ngSwitchCase="'linking'" class="network-checking"><mat-spinner diameter="14"></mat-spinner> Sharing the link to {{ p.title }} with {{ counterpartLabel() }}…</span>
              <ng-container *ngSwitchCase="'error'">
                <span>Could not check whether {{ p.title }} is referred to {{ counterpartLabel() }}.</span>
                <div class="ask-choices">
                  <button type="button" class="not-patient" (click)="retryPrompt(row.message.id, p)">Try again</button>
                  <button type="button" class="not-patient" (click)="notNow(row.message.id, p)">Not now</button>
                </div>
              </ng-container>
              <ng-container *ngSwitchDefault>
                <span>{{ counterpartLabel() }} doesn’t have <b>{{ p.title }}</b> yet, so the link stays in your practice.
                  <ng-container *ngIf="canRefer">Refer them?</ng-container>
                  <ng-container *ngIf="!canRefer">Ask a doctor in your practice to refer {{ p.title }}.</ng-container></span>
                <div class="ask-choices">
                  <button type="button" class="refer-now" *ngIf="canRefer" (click)="referFromChat(row.message.id, p)"><mat-icon>share</mat-icon>Send referral</button>
                  <button type="button" class="not-patient" (click)="notNow(row.message.id, p)">Not now</button>
                </div>
              </ng-container>
            </ng-container>
          </div>
        </div>

        <!-- MedicAI Connect chat: a patient linked here that Connect does not have yet. Sender only. -->
        <div class="ask network-ask" *ngIf="connectPromptsFor(row.message.id).length" role="status">
          <div class="ask-title"><mat-icon>help_outline</mat-icon> Only you can see this</div>
          <div class="ask-row" *ngFor="let p of connectPromptsFor(row.message.id); trackBy: trackConnectPrompt">
            <ng-container [ngSwitch]="p.state">
              <span *ngSwitchCase="'checking'" class="network-checking"><mat-spinner diameter="14"></mat-spinner> Checking whether MedicAI Connect has {{ p.title }}…</span>
              <span *ngSwitchCase="'sending'" class="network-checking"><mat-spinner diameter="14"></mat-spinner> Sending {{ p.title }} to MedicAI Connect…</span>
              <ng-container *ngSwitchCase="'sent'">
                <span>Sent <b>{{ p.title }}</b> to MedicAI Connect. Your billing team will have them shortly.</span>
                <div class="ask-choices">
                  <button type="button" class="not-patient" (click)="dismissConnect(row.message.id, p)">OK</button>
                </div>
              </ng-container>
              <ng-container *ngSwitchCase="'error'">
                <span>Could not reach MedicAI Connect about {{ p.title }}.</span>
                <div class="ask-choices">
                  <button type="button" class="not-patient" (click)="retryConnect(row.message.id, p)">Try again</button>
                  <button type="button" class="not-patient" (click)="dismissConnect(row.message.id, p)">Not now</button>
                </div>
              </ng-container>
              <ng-container *ngSwitchDefault>
                <span>MedicAI Connect doesn’t have <b>{{ p.title }}</b> yet. Send them to your billing team? Their details and
                  ICD codes go, no procedure codes.</span>
                <div class="ask-choices">
                  <button type="button" class="refer-now" (click)="sendToConnect(row.message.id, p)"><mat-icon>send</mat-icon>Send patient</button>
                  <button type="button" class="not-patient" (click)="dismissConnect(row.message.id, p)">Not now</button>
                </div>
              </ng-container>
            </ng-container>
          </div>
        </div>

        <div class="ask" *ngIf="askFor(row.message.id).length" role="status">
          <div class="ask-title"><mat-icon>help_outline</mat-icon> Only you can see this</div>
          <div class="ask-row" *ngFor="let d of askFor(row.message.id)">
            <span>Did you mean a patient by “{{ d.text }}”?</span>
            <div class="ask-choices">
              <button type="button" class="candidate" *ngFor="let c of d.candidates" (click)="linkFromAsk(row.message, d, c)">
                <span class="who">
                  <span class="primary"><b>{{ c.listTitle }}</b><span class="aside" *ngIf="c.number">({{ c.number }})</span></span>
                  <span class="secondary" *ngIf="c.dob">{{ c.dob }}</span>
                </span>
              </button>
              <button type="button" class="not-patient" (click)="answerAsk(row.message.id, d)">Not a patient</button>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- The end of a run of filed messages: the line closes on how many were filed, and to whom. -->
      <div class="filed-summary" *ngIf="row.kind === 'filed'">
        <span class="filed-text">{{ row.count }} messages filed to</span>
        <button type="button" class="chip" *ngFor="let l of row.links" (click)="openLinked(l.conversationId)"
          [matTooltip]="'Open ' + (l.name || 'the patient') + '’s chat'">
          <mat-icon>personal_injury</mat-icon>{{ l.name || 'Patient' }}
        </button>
      </div>
      <!-- Still filing: the line runs on down to the filing bar over the composer. -->
      <div class="filing-open" *ngIf="row.kind === 'filing-open'" aria-hidden="true"></div>
    </ng-container>

    <!-- Scrolled up: a round button down to the latest, with how many new messages wait there (as WhatsApp). -->
    <div class="jump-down-anchor" *ngIf="jumpDown || isHistoric()">
      <button type="button" class="jump-down" (click)="jumpToLatest()" aria-label="Go to the latest message">
        <mat-icon>keyboard_double_arrow_down</mat-icon>
        <span class="jump-count" *ngIf="newMessagesBelow(vm.thread?.messages ?? [], vm.me) as n">{{ n > 99 ? '99+' : n }}</span>
      </button>
    </div>
  </div>

  <div class="typing" [class.visible]="vm.typists.length">
    <span class="typing-dots" aria-hidden="true"><i></i><i></i><i></i></span>
    <ng-container *ngIf="vm.typists.length === 1">{{ vm.typists[0] }} is typing…</ng-container>
    <ng-container *ngIf="vm.typists.length > 1">Several people are typing…</ng-container>
  </div>

  <div class="link-panel" *ngIf="linking">
    <div class="compose-context">
      <mat-icon>person_add_alt</mat-icon>
      <span>Link {{ linking.sender?.name }}'s message to a patient's chat</span>
      <button mat-icon-button (click)="linking = null" aria-label="Cancel"><mat-icon>close</mat-icon></button>
    </div>
    <input #linkInput [(ngModel)]="linkQuery" (input)="onLinkQuery()" (keydown.escape)="linking = null" placeholder="Search patients by name or file number"
      aria-label="Search patients" autocomplete="off" />
    <div class="patient-options" *ngIf="linkResults.length">
      <button type="button" *ngFor="let c of linkResults" (click)="pickLink(c)">
        <span class="who">
          <span class="primary"><b>{{ c.listTitle }}</b><span class="aside" *ngIf="c.number">({{ c.number }})</span></span>
          <span class="secondary" *ngIf="c.dob">{{ c.dob }}</span>
        </span>
        <mat-icon class="pick">visibility</mat-icon>
      </button>
    </div>
    <div class="hint" *ngIf="linkQuery.trim() && !linkResults.length">No patient matches.</div>
  </div>

  <div class="composer" *ngIf="writable()">
    <!--
      "You typed a name — is it this patient?" One panel per name found, with what it heard on the left and the way
      out on the right, over the patients it could be. Picking one turns the name into a `#` mention.
    -->
    <div class="suggestions" *ngIf="suggestions.length && !editing">
      <div class="suggestion" *ngFor="let d of suggestions">
        <div class="suggestion-head">
          <span class="label"><mat-icon>personal_injury</mat-icon>Link “{{ d.text }}” to</span>
          <button mat-icon-button class="dismiss" (click)="dismissSuggestion(d)" matTooltip="Not a patient" aria-label="Not a patient">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <button type="button" class="candidate" *ngFor="let c of d.candidates" (click)="acceptSuggestion(d, c)">
          <span class="who">
            <span class="primary"><b>{{ c.listTitle }}</b><span class="aside" *ngIf="c.number">({{ c.number }})</span></span>
            <span class="secondary" *ngIf="c.dob">{{ c.dob }}</span>
          </span>
          <mat-icon class="pick">visibility</mat-icon>
        </button>
      </div>
    </div>
    <div class="filing-bar" *ngIf="filingText" [title]="filingText">
      <mat-icon>folder_shared</mat-icon>
      <span>{{ filingText }}</span>
      <button mat-button type="button" (click)="endFiling()">End filing</button>
    </div>
    <div class="compose-context" *ngIf="editing || replyTo">
      <mat-icon>{{ editing ? 'edit' : 'reply' }}</mat-icon>
      <span>{{ editing ? 'Editing your message' : 'Replying to ' + (replyTo?.sender?.name ?? '') }}</span>
      <button mat-icon-button (click)="cancelCompose()" aria-label="Cancel"><mat-icon>close</mat-icon></button>
    </div>
    <div class="draft-files" *ngIf="files.length">
      <span class="draft-file" *ngFor="let f of files" [class.failed]="f.state === 'failed'" [matTooltip]="f.error || ''">
        <mat-spinner *ngIf="f.state === 'preparing' || (f.state === 'uploading' && !f.progress)" diameter="14"></mat-spinner>
        <mat-spinner *ngIf="f.state === 'uploading' && f.progress" mode="determinate" [value]="f.progress" diameter="14"></mat-spinner>
        <mat-icon *ngIf="f.state === 'ready'">attach_file</mat-icon>
        <mat-icon *ngIf="f.state === 'failed'">error_outline</mat-icon>
        <span class="name">{{ f.name }}</span>
        <small class="progress" *ngIf="f.state === 'uploading' && f.progress">{{ f.progress }}%</small>
        <button mat-icon-button *ngIf="f.state === 'failed'" (click)="retryFile(f)" [attr.aria-label]="'Retry ' + f.name" matTooltip="Try again"><mat-icon>refresh</mat-icon></button>
        <button mat-icon-button (click)="removeFile(f)" [attr.aria-label]="(f.state === 'uploading' ? 'Cancel ' : 'Remove ') + f.name"><mat-icon>close</mat-icon></button>
      </span>
    </div>
    <!-- While typing: what @, # and / do, as chips that type them (the placeholder is too short to say it). -->
    <div class="trigger-chips" *ngIf="composerFocused && !editing && !picker && !personPicker && !actionPicker && triggerChips().length">
      <button type="button" class="trigger-chip" *ngFor="let c of triggerChips(); trackBy: trackTrigger"
        (mousedown)="$event.preventDefault()" (click)="addTrigger(c.trigger)">
        <span class="trigger">{{ c.trigger }}</span>{{ c.label }}
      </button>
    </div>
    <div class="compose-row">
      <input #filePicker type="file" multiple hidden (change)="onFilesPicked(filePicker)" [accept]="fileAccept" />
      <button mat-icon-button class="attach" *ngIf="!editing && filesAllowed" (click)="pickFiles()" matTooltip="Attach files" aria-label="Attach files"><mat-icon>add_circle_outline</mat-icon></button>
<!--
        The patient rows read like every other patient list in this app: "Surname, Name (file number)" with the date of
        birth under it, and the teal eye on the right. A `#` mention still WRITES "John Smith" into the sentence — that
        is how you would say it — but the list is scanned by surname, so that is how the list is ordered.
      -->
      <!-- The `/` picker: the actions this app offers, each with a line on what it does. -->
      <div class="patient-options picker actions" *ngIf="actionPicker" role="listbox" aria-label="Actions">
        <button type="button" *ngFor="let a of actionPicker.results; let i = index" [class.active]="i === actionPicker.active" role="option"
          (mousedown)="$event.preventDefault()" (click)="pickAction(a)">
          <mat-icon class="action-icon">{{ actionIcon(a) }}</mat-icon>
          <span class="who">
            <span class="primary"><b>{{ a.label }}</b></span>
            <span class="secondary">{{ a.hint }}</span>
          </span>
        </button>
        <div class="hint" *ngIf="!actionPicker.results.length">{{ emptyPickerHint('/', actionPicker.query) }}</div>
      </div>
      <!-- The `@` picker: who may be mentioned here (the hub's list), with their photo or initials. -->
      <div class="patient-options picker people" *ngIf="personPicker" role="listbox" aria-label="People">
        <button type="button" *ngFor="let p of personPicker.results; let i = index" [class.active]="i === personPicker.active" role="option"
          (mousedown)="$event.preventDefault()" (click)="pickPerson(p)">
          <span class="person-avatar" [style.--hue]="personHue(p)">
            <img *ngIf="personPhoto(p) as src" [src]="src" alt="" loading="lazy" />
            <span class="initials" *ngIf="!personPhoto(p)">{{ personInitials(p) }}</span>
          </span>
          <span class="who"><span class="primary"><b>{{ p.name }}</b></span></span>
          <mat-icon class="pick">alternate_email</mat-icon>
        </button>
        <div class="hint" *ngIf="!personPicker.results.length">{{ emptyPickerHint('@', personPicker.query) }}</div>
      </div>
      <div class="patient-options picker" *ngIf="picker" role="listbox" aria-label="Patients">
        <button type="button" *ngFor="let c of picker.results; let i = index" [class.active]="i === picker.active" role="option"
          (mousedown)="$event.preventDefault()" (click)="pickMention(c)">
          <span class="who">
            <span class="primary"><b>{{ c.listTitle }}</b><span class="aside" *ngIf="c.number">({{ c.number }})</span></span>
            <span class="secondary" *ngIf="c.dob">{{ c.dob }}</span>
          </span>
          <mat-icon class="pick">visibility</mat-icon>
        </button>
        <div class="hint" *ngIf="!picker.results.length">{{ emptyPickerHint('#', draft.slice(picker.start + 1, picker.end)) }}</div>
      </div>
      <textarea #composer [(ngModel)]="draft" (keydown)="onKeydown($event)" (input)="onInput()" (click)="onCaret()" (paste)="onPaste($event)" (focus)="composerFocused = true" (blur)="composerFocused = false" rows="1" maxlength="10000"
        cdkTextareaAutosize cdkAutosizeMinRows="1" cdkAutosizeMaxRows="8"
        [placeholder]="placeholder()" aria-label="Message"></textarea>
      <!-- As the referrals dashboard: a filled Send appears once there is something to send. -->
      <button type="button" class="send" *ngIf="editing ? draft.trim() : canSend()" (click)="submit()"
        [attr.aria-label]="editing ? 'Save' : 'Send'" [matTooltip]="editing ? 'Save' : 'Send'"><mat-icon>{{ editing ? 'check' : 'send' }}</mat-icon></button>
    </div>
  </div>

  <div class="composer join" *ngIf="conversation.access === 'public_member'">
    <span>You are reading a public group.</span>
    <button mat-flat-button color="primary" (click)="join()">Join group</button>
  </div>
  <div class="composer join" *ngIf="conversation.access === 'admin'">
    <span>Only members can send messages here.</span>
  </div>
</ng-container>


<!-- Who has read a message (a group's "read by 3 of 4"), opened from its tick. -->
<!-- "Read the letter": the whole summary of one referral card, as the referrals dashboard's letter dialog shows it. -->
<ng-template #letterDialog>
  <ng-container *ngIf="letter as l">
    <h2 mat-dialog-title>{{ l.kind === 'referral_sent' ? 'Referral sent' : l.isReply ? 'Their reply' : 'Referral received' }}</h2>
    <mat-dialog-content class="rec-letter">
      <div class="rec-letter-meta">
        <span>{{ l.at | date: 'd MMM y, HH:mm' }}</span>
        <span class="rec-urgency" [ngClass]="l.urgency">{{ urgencyLabels[l.urgency] }}</span>
        <span *ngIf="l.kind === 'referral_received' && l.referredByName">From {{ l.referredByName }}<ng-container *ngIf="l.practiceName"> · {{ l.practiceName }}</ng-container></span>
      </div>
      <h3 *ngIf="l.reason">Reason for referral</h3>
      <p *ngIf="l.reason">{{ l.reason }}</p>
      <ng-container *ngIf="l.kind === 'referral_sent' && l.summary as sm">
        <h3>Referral note</h3>
        <p class="rec-letter-text">{{ sm.text }}</p>
        <p class="rec-letter-foot">
          <ng-container *ngIf="sm.reviewedByName">Reviewed by {{ sm.reviewedByName }}</ng-container><ng-container *ngIf="sm.aiAssisted"> · AI-assisted</ng-container>
          <ng-container *ngIf="sm.fileCount"> · {{ sm.fileCount }} {{ sm.fileCount === 1 ? 'file' : 'files' }} sent with it</ng-container>
        </p>
      </ng-container>
      <ng-container *ngIf="l.kind === 'referral_received' && l.summaryText">
        <h3>Referral note</h3>
        <p class="rec-letter-text">{{ l.summaryText }}</p>
      </ng-container>
    </mat-dialog-content>
  </ng-container>
  <mat-dialog-actions align="end"><button mat-button mat-dialog-close>Close</button></mat-dialog-actions>
</ng-template>

<!-- The `/ Action` chip: every action, recently used first; one that cannot run here is greyed with the reason. -->
<ng-template #actionSheet>
  <h2 mat-dialog-title>Actions</h2>
  <mat-dialog-content class="action-sheet">
    <button type="button" class="action-tile" *ngFor="let t of actionTiles" [class.unavailable]="!t.available" [disabled]="!t.available"
      (click)="pickFromSheet(t)" [attr.aria-label]="t.action.title + (t.reason ? ': ' + t.reason : '')">
      <mat-icon class="action-tile-icon">{{ actionIcon(t.action) }}</mat-icon>
      <span class="action-tile-title">{{ t.action.title }}</span>
      <span class="action-tile-label">{{ t.action.label }}</span>
      <span class="action-tile-reason" *ngIf="t.reason">{{ t.reason }}</span>
    </button>
  </mat-dialog-content>
</ng-template>

<ng-template #receiptsDialog>
  <h2 mat-dialog-title>Who has seen this</h2>
  <mat-dialog-content class="receipts">
    <ng-container *ngIf="receipts as r">
      <h3 *ngIf="r.read.length">Read</h3>
      <div class="person" *ngFor="let p of r.read"><mat-icon class="read">done_all</mat-icon>{{ p.name }}</div>
      <h3 *ngIf="r.delivered.length">Delivered</h3>
      <div class="person" *ngFor="let p of r.delivered"><mat-icon>done_all</mat-icon>{{ p.name }}</div>
      <h3 *ngIf="r.waiting.length">Not yet delivered</h3>
      <div class="person" *ngFor="let p of r.waiting"><mat-icon>schedule</mat-icon>{{ p.name }}</div>
    </ng-container>
  </mat-dialog-content>
  <mat-dialog-actions align="end"><button mat-button mat-dialog-close>Close</button></mat-dialog-actions>
</ng-template>
